import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, CheckCircle2, XCircle, RefreshCw, ArrowLeft, Shield } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const EmailVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyOtp, resendVerification } = useAuth();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [email, setEmail] = useState('');
  const [verificationMethod, setVerificationMethod] = useState('otp');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [error, setError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Get email from navigation state or session storage
    const stateEmail = location?.state?.email;
    const pendingRegistration = sessionStorage.getItem('pendingRegistration');
    
    if (stateEmail) {
      setEmail(stateEmail);
    } else if (pendingRegistration) {
      const data = JSON.parse(pendingRegistration);
      setEmail(data?.email);
    } else {
      navigate('/register');
      return;
    }

    // Check if user came from email link confirmation
    const params = new URLSearchParams(location.search);
    const token = params?.get('token');
    const type = params?.get('type');
    const tokenHash = params?.get('token_hash');
    
    // Handle different URL formats from Supabase email links
    if (token && type === 'signup') {
      verifyEmailToken(token);
    } else if (tokenHash) {
      verifyEmailTokenHash(tokenHash);
    }
  }, [location, navigate]);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const verifyEmailToken = async (token) => {
    setIsVerifying(true);
    setVerificationMethod('link');
    
    try {
      const { data, error } = await supabase?.auth?.verifyOtp({
        token_hash: token,
        type: 'signup'
      });

      if (error) throw error;

      setVerificationStatus('success');
      sessionStorage.removeItem('pendingRegistration');
      
      setTimeout(() => {
        navigate('/workspace-setup', { 
          state: { welcomeMessage: 'Email verified successfully!' }
        });
      }, 2000);
    } catch (err) {
      console.error('Token verification error:', err);
      setVerificationStatus('error');
      setError(err?.message || 'Invalid or expired verification link. Please try again or use the code sent to your email.');
    } finally {
      setIsVerifying(false);
    }
  };

  const verifyEmailTokenHash = async (tokenHash) => {
    setIsVerifying(true);
    setVerificationMethod('link');
    
    try {
      const { data, error } = await supabase?.auth?.verifyOtp({
        token_hash: tokenHash,
        type: 'signup'
      });

      if (error) throw error;

      setVerificationStatus('success');
      sessionStorage.removeItem('pendingRegistration');
      
      setTimeout(() => {
        navigate('/workspace-setup', { 
          state: { welcomeMessage: 'Email verified successfully!' }
        });
      }, 2000);
    } catch (err) {
      console.error('Token hash verification error:', err);
      setVerificationStatus('error');
      setError(err?.message || 'Invalid or expired verification link. Please try again or use the code sent to your email.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/?.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs?.current?.[index + 1]?.focus();
    }

    if (index === 5 && value && newOtp?.every(digit => digit)) {
      handleVerifyOtp(newOtp?.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e?.key === 'Backspace' && !otp?.[index] && index > 0) {
      inputRefs?.current?.[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e?.preventDefault();
    const pastedData = e?.clipboardData?.getData('text')?.slice(0, 6);
    if (!/^\d+$/?.test(pastedData)) return;

    const newOtp = pastedData?.split('');
    while (newOtp?.length < 6) newOtp?.push('');
    setOtp(newOtp);

    if (pastedData?.length === 6) {
      handleVerifyOtp(pastedData);
    }
  };

  const handleVerifyOtp = async (otpString) => {
    const otpCode = otpString || otp?.join('');
    
    if (otpCode?.length !== 6) {
      setError('Please enter a complete 6-digit code');
      return;
    }

    if (!email) {
      setError('Email address not found. Please go back and register again.');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const { data, error } = await verifyOtp(email, otpCode);

      if (error) throw error;

      setVerificationStatus('success');
      sessionStorage.removeItem('pendingRegistration');
      
      setTimeout(() => {
        navigate('/workspace-setup', { 
          state: { welcomeMessage: 'Email verified successfully!' }
        });
      }, 2000);
    } catch (err) {
      console.error('OTP verification error:', err);
      setError(err?.message || 'Invalid verification code. Please check the code and try again.');
      setVerificationStatus('error');
      setOtp(['', '', '', '', '', '']);
      
      if (inputRefs?.current?.[0]) {
        inputRefs?.current?.[0]?.focus();
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCountdown > 0) return;

    if (!email) {
      setError('Email address not found. Please go back and register again.');
      return;
    }

    setError('');
    
    try {
      await resendVerification(email);

      setResendCountdown(60);
      setOtp(['', '', '', '', '', '']);
      setVerificationStatus(null);
      
      // Show success message
      const successMessage = verificationMethod === 'otp' ?'Verification code sent! Please check your email.' :'Verification email sent! Please check your inbox.';
      
      setError(''); // Clear any errors
      
      // Optional: You could add a temporary success message
      setTimeout(() => {
        console.log(successMessage);
      }, 100);
    } catch (err) {
      console.error('Resend error:', err);
      setError(err?.message || 'Failed to resend verification. Please try again.');
    }
  };

  const handleChangeEmail = () => {
    sessionStorage.removeItem('pendingRegistration');
    navigate('/register');
  };

  const toggleVerificationMethod = () => {
    setVerificationMethod(prev => prev === 'otp' ? 'link' : 'otp');
    setOtp(['', '', '', '', '', '']);
    setVerificationStatus(null);
    setError('');
  };

  if (verificationStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Email Verified!</h2>
          <p className="text-gray-600 mb-6">
            Your account has been successfully verified. Redirecting to workspace setup...
          </p>
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Verify Your Email</h1>
          <p className="text-gray-600">
            We've sent a verification {verificationMethod === 'otp' ? 'code' : 'link'} to
          </p>
          <p className="text-blue-600 font-semibold mt-1">{email}</p>
        </div>

        <div className="flex items-center justify-center mb-6">
          <button
            onClick={toggleVerificationMethod}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {verificationMethod === 'otp' ? 'Use Email Link Instead' : 'Use Verification Code Instead'}
          </button>
        </div>

        {verificationMethod === 'otp' ? (
          <>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                Enter 6-Digit Code
              </label>
              <div className="flex justify-center gap-2" onPaste={handlePaste}>
                {otp?.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e?.target?.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                    disabled={isVerifying}
                  />
                ))}
              </div>
            </div>

            <button
              onClick={() => handleVerifyOtp()}
              disabled={isVerifying || otp?.some(digit => !digit)}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-4"
            >
              {isVerifying ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Verifying...
                </span>
              ) : (
                'Verify Code'
              )}
            </button>
          </>
        ) : (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">Check Your Email</p>
                <p className="text-sm text-gray-600">
                  Click the verification link in the email we sent you to complete your registration.
                  If you don't see it, check your spam folder. The link expires in 24 hours.
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900 mb-1">Verification Failed</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="text-center mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Didn't receive the {verificationMethod === 'otp' ? 'code' : 'email'}?
          </p>
          <button
            onClick={handleResendCode}
            disabled={resendCountdown > 0}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            {resendCountdown > 0 ? (
              `Resend in ${resendCountdown}s`
            ) : (
              <span className="flex items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4" />
                Resend {verificationMethod === 'otp' ? 'Code' : 'Email'}
              </span>
            )}
          </button>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-xs font-semibold text-gray-700 mb-2">💡 Troubleshooting Tips:</p>
          <ul className="text-xs text-gray-600 space-y-1">
            <li>• Check your spam or junk folder</li>
            <li>• Make sure {email} is correct</li>
            <li>• Verification codes expire after 10 minutes</li>
            <li>• Email links are valid for 24 hours</li>
            <li>• Try switching between code and link verification</li>
          </ul>
        </div>

        <button
          onClick={handleChangeEmail}
          className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-gray-800 py-2 text-sm font-medium transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Change Email Address
        </button>
      </div>
    </div>
  );
};

export default EmailVerification;